// src/services/appointmentService.js
import { tenantDataService } from './tenantDataService';

const COLLECTION_NAME = 'appointments';

class AppointmentService {
  // Get all appointments
  async getAllAppointments() {
    return await tenantDataService.getAll(COLLECTION_NAME);
  }

  // Get appointment by ID
  async getAppointmentById(id) {
    return await tenantDataService.getById(COLLECTION_NAME, id);
  }

  // Create new appointment
  async createAppointment(appointmentData) {
    return await tenantDataService.create(COLLECTION_NAME, appointmentData);
  }

  // Update appointment
  async updateAppointment(id, appointmentData) {
    return await tenantDataService.update(COLLECTION_NAME, id, appointmentData);
  }

  // Delete appointment
  async deleteAppointment(id) {
    return await tenantDataService.delete(COLLECTION_NAME, id);
  }

  // Delete future appointments in a series
  async deleteFutureAppointments(seriesId, fromDate) {
    try {
      // Find all appointments in the series from the given date onwards
      const futureAppointments = await tenantDataService.query(COLLECTION_NAME, [
        ['seriesId', '==', seriesId],
        ['date', '>=', fromDate]
      ]);

      if (futureAppointments.length === 0) {
        console.log(`No future appointments found for series ${seriesId} from ${fromDate}`);
        return 0;
      }

      // Get the IDs of the documents to delete
      const idsToDelete = futureAppointments.map(appt => appt.id);

      // Use batch delete for efficiency
      await tenantDataService.batchDelete(COLLECTION_NAME, idsToDelete);

      console.log(`Successfully deleted ${idsToDelete.length} future appointments for series ${seriesId}`);
      return idsToDelete.length;
    } catch (error) {
      console.error(`Failed to delete future appointments for series ${seriesId}:`, error);
      throw error;
    }
  }

  // Get appointments by date range
  async getAppointmentsByDateRange(startDate, endDate) {
    return await tenantDataService.query(COLLECTION_NAME, [
      ['date', '>=', startDate],
      ['date', '<=', endDate]
    ], 'date', 'asc');
  }

  // Get appointments by client ID
  async getAppointmentsByClient(clientId) {
    return await tenantDataService.query(COLLECTION_NAME, [
      ['clientId', '==', clientId]
    ], 'date', 'desc');
  }

  // Get appointments by staff member
  async getAppointmentsByStaff(staffId) {
    return await tenantDataService.query(COLLECTION_NAME, [
      ['staffId', '==', staffId]
    ], 'date', 'asc');
  }

  // Get appointments by room
  async getAppointmentsByRoom(roomId) {
    return await tenantDataService.query(COLLECTION_NAME, [
      ['roomId', '==', roomId]
    ], 'date', 'asc');
  }

  // Get appointments by status
  async getAppointmentsByStatus(status) {
    return await tenantDataService.query(COLLECTION_NAME, [
      ['status', '==', status]
    ], 'date', 'asc');
  }

  // Get today's appointments
  async getTodaysAppointments() {
    const today = new Date().toISOString().split('T')[0];
    return await tenantDataService.query(COLLECTION_NAME, [
      ['date', '==', today]
    ], 'time', 'asc');
  }

  // Get upcoming appointments
  async getUpcomingAppointments(limit = 10) {
    const today = new Date().toISOString().split('T')[0];
    return await tenantDataService.query(COLLECTION_NAME, [
      ['date', '>=', today]
    ], 'date', 'asc');
  }

  // Subscribe to appointment changes
  subscribeToAppointments(callback, conditions = []) {
    return tenantDataService.subscribe(COLLECTION_NAME, callback, conditions);
  }

  // Unsubscribe from appointment changes
  unsubscribeFromAppointments(listenerId) {
    tenantDataService.unsubscribe(listenerId);
  }

}

const appointmentService = new AppointmentService();
export { appointmentService };
export default appointmentService;
